import { Box, ButtonBase, Drawer, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  ChevronRightIcon,
  LimitsIcon,
  RenameIcon,
  TrashIcon,
} from "../../../icons";
import { UserCard } from "../../../services/cards/mocks";

interface CardSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  card: UserCard;
  onBlock: () => void;
}

interface SettingRow {
  id: string;
  label: string;
  hint?: string;
  icon: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}

export function CardSettingsDrawer({
  open,
  onClose,
  card,
  onBlock,
}: CardSettingsDrawerProps) {
  const { t } = useTranslation("cards");
  const rows: SettingRow[] = [
    {
      id: "rename",
      label: t("settings.rename"),
      hint: card.name,
      icon: <RenameIcon color="#8AB4FF" />,
      onClick: () => {
        enqueueSnackbar(t("settings.rename_soon"), {
          variant: "info",
        });
      },
    },
    {
      id: "limits",
      label: t("settings.limits"),
      hint: card.limits
        ? t("settings.limits_hint", { amount: `$${card.limits.monthly}` })
        : t("settings.limits_none"),
      icon: <LimitsIcon color="#8AB4FF" />,
      onClick: () => {
        enqueueSnackbar(t("settings.limits_soon"), {
          variant: "info",
        });
      },
    },
    {
      id: "block",
      label: t("settings.block"),
      hint: t("settings.block_hint"),
      icon: <TrashIcon color="#FF7A7A" />,
      danger: true,
      onClick: () => {
        onClose();
        setTimeout(() => onBlock(), 220);
      },
    },
  ];

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" },
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: "secondary.main",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "10px 16px 28px",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={(theme) => ({
          width: "44px",
          height: "4px",
          borderRadius: "4px",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          margin: "0 auto 14px",
        })}
      />

      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          textAlign: "center",
          marginBottom: "4px",
          color: "text.primary",
        }}
      >
        {t("settings.title")}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: "text.secondary",
          textAlign: "center",
          marginBottom: "18px",
        }}
      >
        {card.name} · •••• {card.lastFour}
      </Typography>

      <Box sx={{ display: "grid", gap: "8px" }}>
        {rows.map((row) => (
          <ButtonBase
            key={row.id}
            onClick={row.onClick}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              gap: "14px",
              width: "100%",
              padding: "12px 14px",
              borderRadius: "18px",
              bgcolor: row.danger
                ? "rgba(255, 90, 90, 0.08)"
                : theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.04)"
                : "rgba(0, 0, 0, 0.04)",
              transition: "background 0.15s",
              "&:active": {
                bgcolor: row.danger
                  ? "rgba(255, 90, 90, 0.14)"
                  : theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.08)",
              },
            })}
          >
            <Box
              sx={{
                width: "42px",
                height: "42px",
                borderRadius: "14px",
                bgcolor: row.danger
                  ? "rgba(255, 90, 90, 0.14)"
                  : "rgba(51, 119, 255, 0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {row.icon}
            </Box>

            <Box sx={{ flex: 1, textAlign: "left", minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: row.danger ? "#FF7A7A" : "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {row.label}
              </Typography>
              {row.hint && (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "text.secondary",
                    marginTop: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {row.hint}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                color: "text.secondary",
                opacity: 0.6,
                display: "flex",
                flexShrink: 0,
              }}
            >
              <ChevronRightIcon />
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Drawer>
  );
}
